import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Maximize2, MapPin, Pencil, BarChart3, SlidersHorizontal, ChevronDown, Bell, Clock3, MessageSquare, Send, X } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import MapOverviewTraffic from "src/newComponents/dashboard/MapOverviewTraffic";
import TopRankingCard from "src/newComponents/dashboard/TopRankingCard";
import useTopRanking from "src/hooks/useTopRanking";
import { RouteDeviationTrendChart } from "src/newComponents/dashboard/RouteDeviationTrendChart";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const kpiCards = [
  { label: "Volume de Trafic", value: "17765", sub: "", style: "from-emerald-100 to-emerald-200 border-emerald-200 text-emerald-900" },
  { label: "Vitesse Moyenne", value: "31 km/h", sub: "", style: "from-cyan-100 to-cyan-200 border-cyan-200 text-cyan-900" },
  { label: "Temps de Parcours", value: "21 min", sub: "", style: "from-blue-100 to-blue-200 border-blue-200 text-blue-900" },
  { label: "Nombre d'Accidents", value: "19", sub: "", style: "from-teal-600 to-teal-700 border-teal-700 text-white" },
  { label: "Taux de Conformité", value: "87%", sub: "", style: "from-indigo-700 to-indigo-800 border-indigo-800 text-white" },
  { label: "véhicules par mètre", value: "87%", sub: "", style: "from-indigo-700 to-indigo-800 border-indigo-800 text-white" },
  { label: "Vitesse maximale", value: "67 km/h", sub: "", style: "from-rose-700 to-rose-800 border-rose-800 text-white" },
  { label: "SOS", value: "15", sub: "", style: "from-red-700 to-red-800 border-red-800 text-white" },

];

const trendData = [
  { name: "Jan", green: 40, red: 60 },
  { name: "Fév", green: 35, red: 45 },
  { name: "Mar", green: 55, red: 50 },
  { name: "Avr", green: 20, red: 68 },
  { name: "Mai", green: 52, red: 38 },
  { name: "Juin", green: 45, red: 60 },
  { name: "Juil", green: 62, red: 56 },
  { name: "AoÃ»", green: 58, red: 52 },
];

const multiTrendData = [
  { name: "00h", volumeTrafic: 120, vitesseMoyenne: 65 },
  { name: "01h", volumeTrafic: 90, vitesseMoyenne: 70 },
  { name: "02h", volumeTrafic: 60, vitesseMoyenne: 75 },
  { name: "03h", volumeTrafic: 40, vitesseMoyenne: 78 },
  { name: "04h", volumeTrafic: 30, vitesseMoyenne: 80 },
  { name: "05h", volumeTrafic: 50, vitesseMoyenne: 72 },
  { name: "06h", volumeTrafic: 200, vitesseMoyenne: 55 },
  { name: "07h", volumeTrafic: 350, vitesseMoyenne: 45 },
  { name: "08h", volumeTrafic: 500, vitesseMoyenne: 40 },
  { name: "09h", volumeTrafic: 420, vitesseMoyenne: 48 },
  { name: "10h", volumeTrafic: 300, vitesseMoyenne: 55 },
  { name: "11h", volumeTrafic: 280, vitesseMoyenne: 60 },
  { name: "12h", volumeTrafic: 320, vitesseMoyenne: 58 },
  { name: "13h", volumeTrafic: 290, vitesseMoyenne: 62 },
  { name: "14h", volumeTrafic: 310, vitesseMoyenne: 59 },
  { name: "15h", volumeTrafic: 360, vitesseMoyenne: 52 },
  { name: "16h", volumeTrafic: 450, vitesseMoyenne: 46 },
  { name: "17h", volumeTrafic: 520, vitesseMoyenne: 38 },
  { name: "18h", volumeTrafic: 480, vitesseMoyenne: 42 },
  { name: "19h", volumeTrafic: 390, vitesseMoyenne: 50 },
  { name: "20h", volumeTrafic: 300, vitesseMoyenne: 58 },
  { name: "21h", volumeTrafic: 220, vitesseMoyenne: 65 },
  { name: "22h", volumeTrafic: 160, vitesseMoyenne: 70 },
  { name: "23h", volumeTrafic: 130, vitesseMoyenne: 72 },
];


const WEEKDAY_OPTIONS = [
  { value: "all", label: "Tous les jours" },
  { value: "monday", label: "Tous les lundis" },
  { value: "tuesday", label: "Tous les mardis" },
  { value: "wednesday", label: "Tous les mercredis" },
  { value: "thursday", label: "Tous les jeudis" },
  { value: "friday", label: "Tous les vendredis" },
  { value: "saturday", label: "Tous les samedis" },
  { value: "sunday", label: "Tous les dimanches" },
];

const DAY_FACTORS = {
  all: { volume: 1, speed: 1, incidents: 1, deviations: 1 },
  monday: { volume: 1.08, speed: 0.96, incidents: 1.07, deviations: 1.06 },
  tuesday: { volume: 1.04, speed: 0.98, incidents: 1.03, deviations: 1.02 },
  wednesday: { volume: 1.02, speed: 0.99, incidents: 1.01, deviations: 1.01 },
  thursday: { volume: 1.05, speed: 0.97, incidents: 1.04, deviations: 1.04 },
  friday: { volume: 1.15, speed: 0.93, incidents: 1.14, deviations: 1.15 },
  saturday: { volume: 0.86, speed: 1.07, incidents: 0.88, deviations: 0.9 },
  sunday: { volume: 0.74, speed: 1.11, incidents: 0.76, deviations: 0.78 },
};
const INCIDENT_SERIES = [
  { key: "hardAcceleration", label: "Accélération brusque", color: "#f97316" },
  { key: "overspeed", label: "Excès de vitesse", color: "#22c55e" },
  { key: "hardBraking", label: "Freinage brusque", color: "#eab308" },
  { key: "panicButton", label: "Panic bouton", color: "#ef4444" },
  { key: "speedLimitViolation", label: "Violation limite vitesse", color: "#3b82f6" },
];

const incidentTrendData = [
  { hour: "00h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "04h", panicButton: 3, hardAcceleration: 4, hardBraking: 3, overspeed: 5, speedLimitViolation: 4 },
  { hour: "08h", panicButton: 8, hardAcceleration: 8, hardBraking: 7, overspeed: 11, speedLimitViolation: 9 },
  { hour: "12h", panicButton: 12, hardAcceleration: 11, hardBraking: 10, overspeed: 12, speedLimitViolation: 11 },
  { hour: "16h", panicButton: 10, hardAcceleration: 10, hardBraking: 9, overspeed: 12, speedLimitViolation: 10 },
  { hour: "20h", panicButton: 5, hardAcceleration: 5, hardBraking: 4, overspeed: 6, speedLimitViolation: 5 },
];

const densityBars = [
  { name: "10", value: 3 },
  { name: "15", value: 5 },
  { name: "21", value: 4 },
  { name: "34", value: 8 },
  { name: "45", value: 6 },
  { name: "51", value: 9 },
  { name: "59", value: 7 },
  { name: "63", value: 5 },
  { name: "70", value: 8 },
];

const deviationSummaryCards = [
  { key: "reportedIncidents", title: "Incidents signalés", value: 21, unit: "" },
  { key: "delayDuration", title: "Durée des retards", value: 74, unit: "min" },
  { key: "userSatisfaction", title: "Satisfaction usagers", value: 68, unit: "%" },
  { key: "offRouteDrivers", title: "Taux de non respect des trajets prévus", value: 14, unit: "" },
];

const speedLimitationZones = [
  {
    limitation: "50",
    title: "50 km/h",
    vehicles: 45,
    maxSpeed: 68,
    avgSpeed: 49,
    trendColor: "#22c55e",
    trendData: [
      { name: "00h", value: 5 },
      { name: "04h", value: 8 },
      { name: "08h", value: 15 },
      { name: "12h", value: 20 },
      { name: "16h", value: 18 },
      { name: "20h", value: 10 },
    ],
    colorTheme: "green",
    cardClass: "bg-green-50 border-green-200",
    titleClass: "text-green-800",
  },
  {
    limitation: "80",
    title: "80 km/h",
    vehicles: 80,
    maxSpeed: 102,
    avgSpeed: 76,
    trendColor: "#eab308",
    trendData: [
      { name: "00h", value: 6 },
      { name: "04h", value: 10 },
      { name: "08h", value: 16 },
      { name: "12h", value: 22 },
      { name: "16h", value: 19 },
      { name: "20h", value: 12 },
    ],
    colorTheme: "yellow",
    cardClass: "bg-yellow-50 border-yellow-200",
    titleClass: "text-yellow-800",
  },
  {
    limitation: "90",
    title: "90 km/h",
    vehicles: 65,
    maxSpeed: 121,
    avgSpeed: 84,
    trendColor: "#ef4444",
    trendData: [
      { name: "00h", value: 5 },
      { name: "04h", value: 8 },
      { name: "08h", value: 15 },
      { name: "12h", value: 20 },
      { name: "16h", value: 18 },
      { name: "20h", value: 10 },
    ],
    colorTheme: "red",
    cardClass: "bg-red-50 border-red-200",
    titleClass: "text-red-800",
  },
  {
    limitation: "110",
    title: "110 km/h",
    vehicles: 120,
    maxSpeed: 137,
    avgSpeed: 103,
    trendColor: "#8b5cf6",
    trendData: [
      { name: "00h", value: 7 },
      { name: "04h", value: 11 },
      { name: "08h", value: 17 },
      { name: "12h", value: 23 },
      { name: "16h", value: 20 },
      { name: "20h", value: 13 },
    ],
    colorTheme: "purple",
    cardClass: "bg-purple-50 border-purple-200",
    titleClass: "text-purple-800",
  },
];

const conformiteItems = [
  { label: "Véhicules ne respectant pas les limitations de vitesse", value: 34, color: "bg-red-500" },
  { label: "Véhicules roulants non-conformes", value: 19, color: "bg-orange-500" },
  { label: "Nombre de non-respects des feux de signalisation", value: 27, color: "bg-amber-500" },
];

const ecologieItems = [
  { label: "Usagers en transports en commun ou mode écologique", value: "42%" },
  { label: "Taux d'utilisateurs de vélo ou de covoiturage", value: "18%" },
];

const feedbackDataLeft = [
  { name: "A", value: 45, color: "#10E3BB" },
  { name: "B", value: 24, color: "#FFD21C" },
  { name: "C", value: 18, color: "#E849A0" },
  { name: "D", value: 13, color: "#19B2FF" },
];

const feedbackDataRight = [
  { name: "A", value: 52, color: "#10E3BB" },
  { name: "B", value: 19, color: "#FFD21C" },
  { name: "C", value: 16, color: "#E849A0" },
  { name: "D", value: 13, color: "#7D73FF" },
];

const alertItems = [
  { id: 1, color: "#ef4444", label: "Niveau critique", arrivedAt: "08:12", status: "Urgent", vehicle: "Véhicule CE-002" },
  { id: 2, color: "#f97316", label: "Congestion du trafic", arrivedAt: "08:27", status: "Moyen", vehicle: "Véhicule LT-004" },
  { id: 3, color: "#eab308", label: "Péage autoroutier", arrivedAt: "08:41", status: "Normal", vehicle: "Véhicule OU-003" },
  { id: 4, color: "#fb7185", label: "Priorité élevée", arrivedAt: "09:33", status: "Urgent", vehicle: "Véhicule CE-001" },
  { id: 5, color: "#22c55e", label: "Signalement social", arrivedAt: "10:05", status: "Normal", vehicle: "Véhicule LT-001" },
  { id: 6, color: "#ef4444", label: "Alerte collision", arrivedAt: "10:14", status: "Urgent", vehicle: "Véhicule CE-004" },
  { id: 7, color: "#f97316", label: "Bouchon axe Nord", arrivedAt: "10:22", status: "Moyen", vehicle: "Véhicule LT-006" },
  { id: 8, color: "#eab308", label: "Ralentissement péage", arrivedAt: "10:37", status: "Normal", vehicle: "Véhicule OU-006" },
  { id: 9, color: "#fb7185", label: "Arrêt non autorisé", arrivedAt: "10:45", status: "Moyen", vehicle: "Véhicule CE-007" },
  { id: 10, color: "#22c55e", label: "Signalement usager", arrivedAt: "10:58", status: "Info", vehicle: "Véhicule LT-008" },
  { id: 11, color: "#ef4444", label: "Incident majeur", arrivedAt: "11:06", status: "Urgent", vehicle: "Véhicule CE-010" },
  { id: 12, color: "#f97316", label: "Déviation saturée", arrivedAt: "11:20", status: "Moyen", vehicle: "Véhicule OU-009" },
  { id: 13, color: "#eab308", label: "Alerte signalisation", arrivedAt: "11:33", status: "Normal", vehicle: "Véhicule LT-011" },
  { id: 14, color: "#fb7185", label: "Risque de blocage", arrivedAt: "11:47", status: "Urgent", vehicle: "Véhicule CE-012" },
];

const commentItems = [
  { id: 101, color: "#10b981", label: "Le trafic est fluide ce matin.", arrivedAt: "08:49", author: "Usager A" },
  { id: 102, color: "#38bdf8", label: "Ralentissement au rond-point central.", arrivedAt: "09:02", author: "Usager B" },
  { id: 103, color: "#f59e0b", label: "Signalisation peu visible à l'entrée Est.", arrivedAt: "09:15", author: "Usager C" },
  { id: 104, color: "#a78bfa", label: "Déviation bien indiquée mais trop longue.", arrivedAt: "09:48", author: "Usager D" },
];

const incidentRows = [
  {
    vehicle: "Véhicule CE-002",
    incidentDate: "2026-02-19 08:12",
    entryDate: "2026-02-19 08:12",
    exitDate: "2026-02-19 08:32",
    location: "Carrefour Logpom",
    type: "Panic bouton",
  },
  {
    vehicle: "Véhicule LT-004",
    incidentDate: "2026-02-19 08:48",
    entryDate: "2026-02-19 08:48",
    exitDate: "2026-02-19 09:10",
    location: "Boulevard Deido",
    type: "Accélération brusque",
  },
  {
    vehicle: "Véhicule OU-003",
    incidentDate: "2026-02-19 09:22",
    entryDate: "2026-02-19 09:22",
    exitDate: "2026-02-19 09:44",
    location: "Rond-point Bonamoussadi",
    type: "Freinage brusque",
  },
  {
    vehicle: "Véhicule CE-001",
    incidentDate: "2026-02-19 09:55",
    entryDate: "2026-02-19 09:55",
    exitDate: "2026-02-19 10:21",
    location: "Axe Douala V",
    type: "Excès de vitesse",
  },
  {
    vehicle: "Véhicule LT-001",
    incidentDate: "2026-02-19 10:35",
    entryDate: "2026-02-19 10:35",
    exitDate: "2026-02-19 11:02",
    location: "Entrée Zone École",
    type: "Violation limite vitesse",
  },
];

const INCIDENTS_PAGE_SIZE = 4;

const tileSelect = (
  <span className="inline-flex items-center gap-1 rounded-full bg-secondary border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
    <ChevronDown className="w-3 h-3" />
  </span>
);

const DarkCard = ({ title, action, children, className = "" }) => (
  <div className={`rounded-2xl bg-card border border-border shadow-sm ${className}`}>
    <div className="px-4 pt-3 pb-1 flex items-center justify-between">
      <h3 className="text-base md:text-lg font-semibold text-foreground tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-4 pt-2">{children}</div>
  </div>
);

const EMPTY_FILTERS = {};

const DeviationTopRankingCard = () => {
  const options = [
    {
      value: "driversOffRoute",
      label: "Chauffeur aux Trajets Déviés",
      category: "drivers",
      metric: "deviations",
      valueLabel: "Déviations",
    }
  ];
  const [selectedType, setSelectedType] = useState(options[0].value);
  const selectedOption = options.find((opt) => opt.value === selectedType) || options[0];

  const { data, loading, error } = useTopRanking({
    category: selectedOption.category,
    metric: selectedOption.metric,
    topX: 20,
    period: "week",
    filters: selectedOption.filters || EMPTY_FILTERS,
  });

  return (
    <div className="rounded-xl border border-border bg-card p-2">
      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Chargement du classement...</div>
      ) : error ? (
        <div className="text-xs text-red-500 py-2">Erreur de chargement du classement</div>
      ) : (
        <TopRankingCard
          title=""
          data={data}
          topX={5}
          fixedPageSize={5}
          showTopXSelector={false}
          colorTheme="orange"
          compact={true}
          showPagination={true}
          emptyMessage="Aucune donnée disponible"
          headerControls={
            <div className="flex flex-wrap items-center gap-1 min-w-0">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-40 max-w-full text-xs border border-gray-300 rounded px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 truncate min-w-0"
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200 text-[10px] font-semibold shadow-sm max-w-full truncate">
                {selectedOption.label}
              </span>
            </div>
          }
        />
      )}
    </div>
  );
};

const WeekdayFilterSelect = ({ value, onChange }) => (
  <div className="mb-2">
    <select
      value={value}
      onChange={onChange}
      className="h-10 min-w-[180px] rounded-lg border border-border bg-card px-3 pr-8 text-sm leading-5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {WEEKDAY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const LimitationZoneRankingCard = ({ limitation, colorTheme, label }) => {
  const { data, loading, error } = useTopRanking({
    category: "vehicles",
    metric: "violations",
    topX: 10,
    period: "week",
    filters: { limitation },
  });

  if (loading) {
    return <div className="text-xs text-muted-foreground py-2">Chargement du classement...</div>;
  }
  if (error) {
    return <div className="text-xs text-red-500 py-2">Erreur de chargement du classement</div>;
  }

  return (
    <TopRankingCard
      title=""
      data={data}
      topX={5}
      fixedPageSize={5}
      showTopXSelector={false}
      compact={true}
      showPagination={true}
      colorTheme={colorTheme}
      emptyMessage="Aucune donnée disponible"
      headerControls={
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 border text-[10px] font-semibold text-gray-700">
          {label}
        </span>
      }
    />
  );
};

const TrafficPageImpl = (_, ref) => {
  const mapOverviewRef = useRef(null);
  const outletContext = useOutletContext() || {};
  const { allAreas = [], site = null, zone = null } = outletContext;
  const [commentInput, setCommentInput] = useState("");
  const [incidentPage, setIncidentPage] = useState(1);
  const [selectedIncidentSeriesKey, setSelectedIncidentSeriesKey] = useState(null);
  const [weekdayFilter, setWeekdayFilter] = useState("all");
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [alertResponses, setAlertResponses] = useState({});
  const [sentResponses, setSentResponses] = useState({});
  const [alertsModalPage, setAlertsModalPage] = useState(1);

  const filteredMultiTrendData = useMemo(() => {
    const factor = DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all;
    return multiTrendData.map((item) => ({
      ...item,
      volumeTrafic: Math.round(item.volumeTrafic * factor.volume),
      vitesseMoyenne: Math.round(item.vitesseMoyenne * factor.speed),
    }));
  }, [weekdayFilter]);

  const filteredIncidentTrendData = useMemo(() => {
    const factor = DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all;
    return incidentTrendData.map((item) => ({
      ...item,
      panicButton: Math.min(12, Math.max(0, Math.round(item.panicButton * factor.incidents))),
      hardAcceleration: Math.min(12, Math.max(0, Math.round(item.hardAcceleration * factor.incidents))),
      hardBraking: Math.min(12, Math.max(0, Math.round(item.hardBraking * factor.incidents))),
      overspeed: Math.min(12, Math.max(0, Math.round(item.overspeed * factor.incidents))),
      speedLimitViolation: Math.min(12, Math.max(0, Math.round(item.speedLimitViolation * factor.incidents))),
    }));
  }, [weekdayFilter]);

  const activeIncidentSeries = useMemo(
    () => INCIDENT_SERIES.filter((series) => !selectedIncidentSeriesKey || series.key === selectedIncidentSeriesKey),
    [selectedIncidentSeriesKey]
  );

  const selectedIncidentLabel = useMemo(
    () => INCIDENT_SERIES.find((series) => series.key === selectedIncidentSeriesKey)?.label || null,
    [selectedIncidentSeriesKey]
  );

  const filteredIncidentRows = useMemo(() => {
    if (!selectedIncidentLabel) return incidentRows;
    return incidentRows.filter((row) => row.type === selectedIncidentLabel);
  }, [selectedIncidentLabel]);

  const totalIncidentPages = Math.max(1, Math.ceil(filteredIncidentRows.length / INCIDENTS_PAGE_SIZE));
  const paginatedIncidentRows = filteredIncidentRows.slice(
    (incidentPage - 1) * INCIDENTS_PAGE_SIZE,
    incidentPage * INCIDENTS_PAGE_SIZE
  );
  useEffect(() => {
    if (incidentPage > totalIncidentPages) {
      setIncidentPage(totalIncidentPages);
    }
  }, [incidentPage, totalIncidentPages]);

  const alertCommentTypes = useMemo(() => ([
    { key: "alertes", label: "Alertes", count: alertItems.length },
    { key: "commentaires", label: "Commentaires", count: commentItems.length },
  ]), []);

  const alertModalPageSize = 10;
  const modalItems = selectedItemType === "commentaires" ? commentItems : alertItems;
  const totalAlertModalPages = Math.max(1, Math.ceil(alertItems.length / alertModalPageSize));
  const paginatedAlertItems = alertItems.slice(
    (alertsModalPage - 1) * alertModalPageSize,
    alertsModalPage * alertModalPageSize
  );
  const displayedModalItems = selectedItemType === "alertes" ? paginatedAlertItems : modalItems;

  // Timer d'actualisation desactive temporairement

  useImperativeHandle(ref, () => ({
    getMapOverview: () => mapOverviewRef.current,
    getDrawShape: () => mapOverviewRef.current?.handleSetDrawShape,
    getDrawAllAois: () => mapOverviewRef.current?.handleSetDrawAllAois,
  }));

  return (
    <div className="min-h-screen bg-background p-2 md:p-4">
      <div className="relative w-full rounded-[22px] border border-border bg-card p-3 md:p-5 shadow-sm">
        <div className="absolute left-0 top-0 bottom-0 w-6 rounded-l-[22px] " />

        <div className="ml-5">
          <div className="mb-4 flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-4 py-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Tableau de Bord Flux des Trafic</h1>
               
              </div>
            </div>
            {/* <div className="flex items-center gap-2">
              {[MapPin, Pencil, BarChart3, SlidersHorizontal].map((Icon, idx) => (
                <button key={idx} className="h-8 w-8 rounded-full bg-secondary text-muted-foreground inline-flex items-center justify-center hover:bg-secondary/80">
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div> */}
          </div>

          <div className="mb-2 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-1.5">
            {kpiCards.map((card) => (
              <div key={card.label} className={`rounded-2xl border bg-gradient-to-br ${card.style} p-2 shadow-sm`}>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-[12px] font-medium">{card.label}</p>
                </div>
                <p className="text-[18px] leading-none font-bold">{card.value}</p>
                <p className="text-[12px] opacity-80 mt-1">{card.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 xl:col-span-10 space-y-2">

              <div className="grid grid-cols-12">
            
                <div className="col-span-12 lg:col-span-12">
                  <MapOverviewTraffic
                    ref={mapOverviewRef}
                    t={(key) => key}
                    allAreas={allAreas}
                    site={site}
                    zone={zone}
                    weekdayFilter={weekdayFilter}
                    onWeekdayFilterChange={setWeekdayFilter}
                    weekdayOptions={WEEKDAY_OPTIONS}
                  />
                </div>

                
{/* 
                <DarkCard title="Temps de Densité" className="col-span-12 lg:col-span-2">
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 3" />
                        <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="red" name="Tendance densité" stroke="#f97316" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </DarkCard> */}
              </div>

            {/*   <DarkCard
                title="Évolution horaire des Flux de Trafic et de la Vitesse Moyenne"
                className="col-span-12 lg:col-span-6"
              >
                  <WeekdayFilterSelect
                    value={weekdayFilter}
                    onChange={(e) => setWeekdayFilter(e.target.value)}
                  />
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredMultiTrendData}>
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 3" />
                        <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="volumeTrafic" name="Volume de trafic" stroke="#ef4444" strokeWidth={3} dot={false} />
                        <Line type="monotone" dataKey="vitesseMoyenne" name="Vitesse moyenne" stroke="#06b6d4" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </DarkCard> */}

             

              

             {/*  <div className="grid grid-cols-12 gap-3">
                <DarkCard title="Retour des Usagers" className="col-span-12 lg:col-span-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-[190px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={feedbackDataLeft} dataKey="value" innerRadius={44} outerRadius={72}>
                            {feedbackDataLeft.map((d) => (
                              <Cell key={d.name} fill={d.color} />
                            ))}
                          </Pie>
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="h-[190px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={feedbackDataRight} dataKey="value" innerRadius={44} outerRadius={72}>
                            {feedbackDataRight.map((d) => (
                              <Cell key={d.name} fill={d.color} />
                            ))}
                          </Pie>
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="mb-1 block text-xs text-muted-foreground">Commentaires usagers</label>
                    <textarea
                      value={commentInput}
                      onChange={(e) => setCommentInput(e.target.value)}
                      placeholder="Saisir un commentaire sur l'état du trafic..."
                      className="w-full min-h-[74px] rounded-xl border border-border bg-secondary/30 px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </DarkCard>

                <DarkCard title="Liens d'Audit" className="col-span-12 lg:col-span-3">
                  <ul className="space-y-2 text-sm text-foreground">
                    {["DO37A", "2G78", "P2735", "237YC3", "C7UCJ 82"].map((item) => (
                      <li key={item} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2">
                        <span>{item}</span>
                        <span className="text-primary">â€¢</span>
                      </li>
                    ))}
                  </ul>
                </DarkCard>

                <DarkCard title="Liens de Suivi" className="col-span-12 lg:col-span-3">
                  <ul className="space-y-2 text-sm text-foreground">
                    {["Ligne 6 prioritaire", "BSD actif", "SPA", "Test de tempo", "Flux surveillé"].map((item) => (
                      <li key={item} className="rounded-lg bg-secondary px-3 py-2">{item}</li>
                    ))}
                  </ul>
                </DarkCard>
              </div> */}
            </div>
 
            <div className="col-span-12 xl:col-span-2 space-y-3">
             {/*  <DarkCard title="Filtres" action={<Maximize2 className="w-4 h-4 text-muted-foreground" />}>
                <div className="space-y-2 text-sm text-foreground">
                  {["Accidents", "Obstacles", "Travaux routiers"].map((filter) => (
                    <label key={filter} className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2">
                      <input type="checkbox" defaultChecked={filter !== "Travaux routiers"} />
                      <span>{filter}</span>
                    </label>
                  ))}
                </div>
              </DarkCard>

              <DarkCard title="Statistiques Historiques">
                <ul className="space-y-2 text-sm text-foreground">
                  <li className="rounded-lg bg-secondary px-3 py-2 flex justify-between"><span>Semaine préc.</span><b>+6%</b></li>
                  <li className="rounded-lg bg-secondary px-3 py-2 flex justify-between"><span>Mois préc.</span><b>-2%</b></li>
                  <li className="rounded-lg bg-secondary px-3 py-2 flex justify-between"><span>Trimestre</span><b>+11%</b></li>
                </ul>
              </DarkCard> 

              <DarkCard title="Liens Rapides">
                <ul className="space-y-2 text-sm text-foreground">
                  {["Rapports d'Incident", "Ã‰tat des Travaux", "Centre d'Urgence"].map((item) => (
                    <li key={item} className="rounded-lg bg-secondary px-3 py-2">{item}</li>
                  ))}
                </ul>
              </DarkCard>*/}

              <DarkCard title="Alertes & Commentaires">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItemType("alertes");
                    setAlertsModalPage(1);
                    setIsAlertsModalOpen(true);
                  }}
                  className="mb-3 flex w-full items-center justify-between rounded-2xl border border-border bg-card px-3.5 py-2 shadow-sm transition-colors hover:bg-secondary/60"
                >
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Bell className="w-4 h-4 text-red-400" />
                    Alertes actives
                  </div>
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-bold text-red-500">
                    {alertItems.length}
                  </span>
                </button>
                <div className="space-y-3">
                  {alertCommentTypes.map((itemType) => (
                    <div key={`alert-type-${itemType.key}`} className="space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemType(itemType.key);
                          setAlertsModalPage(1);
                          setIsAlertsModalOpen(true);
                        }}
                        className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-left shadow-sm transition-colors hover:bg-secondary/60"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                            {(() => {
                              const TypeIcon = itemType.key === "commentaires" ? MessageSquare : Bell;
                              return <TypeIcon className="w-3.5 h-3.5 shrink-0 text-red-400" />;
                            })()}
                            <span className="truncate font-semibold">{itemType.label}</span>
                          </div>
                          <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-500">
                            {itemType.count}
                          </span>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </DarkCard>
            </div>
          </div>

          {isAlertsModalOpen && selectedItemType && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-2xl rounded-2xl border border-border bg-card shadow-xl">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <h3 className="text-base font-semibold text-foreground">
                    {selectedItemType === "commentaires" ? "Liste des commentaires" : "Liste des alertes"}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAlertsModalOpen(false);
                      setSelectedItemType(null);
                      setAlertsModalPage(1);
                    }}
                    className="rounded-lg border border-border p-1 text-muted-foreground hover:bg-secondary/60"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-[70vh] overflow-auto p-4">
                  {displayedModalItems.map((item) => (
                    <div key={`modal-item-${item.id}`} className="mb-3 rounded-xl border border-border bg-secondary/20 p-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="font-medium text-foreground">{item.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5">
                            <Clock3 className="w-3 h-3" />
                            {selectedItemType === "commentaires" ? "Posté à" : "Survenu à"}: {item.arrivedAt}
                          </span>
                          {selectedItemType === "commentaires" ? (
                            <span className="inline-flex rounded-full bg-card px-2 py-0.5">{item.author}</span>
                          ) : (
                            <>
                              <span className="inline-flex rounded-full bg-card px-2 py-0.5">{item.status}</span>
                              <span className="inline-flex rounded-full bg-card px-2 py-0.5">{item.vehicle}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {selectedItemType === "commentaires" ? (
                        <>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <input
                              type="text"
                              value={alertResponses[item.id] || ""}
                              onChange={(e) =>
                                setAlertResponses((prev) => ({ ...prev, [item.id]: e.target.value }))
                              }
                              placeholder="Répondre au commentaire..."
                              className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setSentResponses((prev) => ({
                                  ...prev,
                                  [item.id]: Boolean((alertResponses[item.id] || "").trim()),
                                }))
                              }
                              className="inline-flex h-10 items-center justify-center gap-1 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700"
                            >
                              <Send className="w-3.5 h-3.5" />
                              Répondre
                            </button>
                          </div>
                          {sentResponses[item.id] && (
                            <p className="mt-2 text-xs text-green-600">Réponse envoyée.</p>
                          )}
                        </>
                      ) : null}
                    </div>
                  ))}
                  {selectedItemType === "alertes" && alertItems.length > alertModalPageSize && (
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <button
                        type="button"
                        onClick={() => setAlertsModalPage((prev) => Math.max(1, prev - 1))}
                        disabled={alertsModalPage === 1}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <span>
                        Page {alertsModalPage}/{totalAlertModalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAlertsModalPage((prev) => Math.min(totalAlertModalPages, prev + 1))}
                        disabled={alertsModalPage === totalAlertModalPages}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

<div className="grid grid-cols-12 gap-3">
                <DarkCard title="Évolution horaire des incidents" className="col-span-12 lg:col-span-6">
                  <WeekdayFilterSelect
                    value={weekdayFilter}
                    onChange={(e) => setWeekdayFilter(e.target.value)}
                  />
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredIncidentTrendData}>
                        <CartesianGrid stroke="#475569" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="hour" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 10 }} domain={[0, 12]} ticks={[0, 3, 6, 9, 12]} />
                        <Tooltip />
                        {activeIncidentSeries.map((series) => (
                          <Line
                            key={`incident-line-${series.key}`}
                            type="monotone"
                            dataKey={series.key}
                            name={series.label}
                            stroke={series.color}
                            strokeWidth={3}
                            dot={{ r: 4, stroke: series.color, strokeWidth: 2, fill: "#fff" }}
                            activeDot={{ r: 5 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedIncidentSeriesKey(null);
                        setIncidentPage(1);
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 transition-colors ${
                        !selectedIncidentSeriesKey
                          ? "border-slate-400 bg-slate-100 text-foreground"
                          : "border-border bg-card text-muted-foreground"
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
                      Tous
                    </button>
                    {INCIDENT_SERIES.map((series) => {
                      const isActive = !selectedIncidentSeriesKey || selectedIncidentSeriesKey === series.key;
                      return (
                        <button
                          key={`incident-legend-${series.key}`}
                          type="button"
                          onClick={() => {
                            setSelectedIncidentSeriesKey((prev) => (prev === series.key ? null : series.key));
                            setIncidentPage(1);
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 transition-colors ${
                            isActive ? "border-slate-400 bg-slate-100 text-foreground" : "border-border bg-card text-muted-foreground"
                          }`}
                        >
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: series.color }} />
                          {series.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 overflow-x-auto rounded-xl border border-border">
                    <table className="min-w-full text-xs">
                      <thead className="bg-secondary/60 text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left">Véhicule</th>
                          <th className="px-3 py-2 text-left">Date incident</th>
                          <th className="px-3 py-2 text-left">Date d'entrée</th>
                          <th className="px-3 py-2 text-left">Date de sortie</th>
                          <th className="px-3 py-2 text-left">Localisation</th>
                          <th className="px-3 py-2 text-left">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedIncidentRows.length > 0 ? (
                          paginatedIncidentRows.map((row, idx) => (
                            <tr key={`${row.vehicle}-${row.incidentDate}-${idx}`} className="border-t border-border">
                              <td className="px-3 py-2">{row.vehicle}</td>
                              <td className="px-3 py-2">{row.incidentDate}</td>
                              <td className="px-3 py-2">{row.entryDate}</td>
                              <td className="px-3 py-2">{row.exitDate}</td>
                              <td className="px-3 py-2">{row.location}</td>
                              <td className="px-3 py-2">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  row.type === "Panic bouton"
                                    ? "bg-red-100 text-red-700"
                                    : row.type === "Accélération brusque"
                                    ? "bg-orange-100 text-orange-700"
                                    : row.type === "Freinage brusque"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : row.type === "Excès de vitesse"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-blue-100 text-blue-700"
                                }`}>
                                  {row.type}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                              Aucune donnée pour ce type
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setIncidentPage((prev) => Math.max(1, prev - 1))}
                      disabled={incidentPage === 1}
                      className="rounded-md border border-border bg-card px-2 py-1 text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <span className="text-muted-foreground">
                      Page {incidentPage}/{totalIncidentPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIncidentPage((prev) => Math.min(totalIncidentPages, prev + 1))}
                      disabled={incidentPage === totalIncidentPages}
                      className="rounded-md border border-border bg-card px-2 py-1 text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </DarkCard>


                <DarkCard title="Déviation" className="col-span-12 lg:col-span-6">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {deviationSummaryCards.map((item) => (
                        <div key={item.key} className="rounded-xl border border-border bg-secondary/30 p-3">
                          <p className="text-xs font-medium text-muted-foreground">{item.title}</p>
                          <p className="mt-1 text-2xl font-bold text-foreground">
                            {item.value}
                            {item.unit ? <span className="ml-1 text-sm font-medium text-muted-foreground">{item.unit}</span> : null}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                      <div className="xl:col-span-2 rounded-xl border border-border bg-secondary/20 p-2">
                        <WeekdayFilterSelect
                          value={weekdayFilter}
                          onChange={(e) => setWeekdayFilter(e.target.value)}
                        />
                        <div className="text-xs font-semibold text-foreground mb-2">
                          Évolution Horaire du Taux de Déviation des Trajets Prévus
                        </div>
                        <RouteDeviationTrendChart
                          dayFactor={(DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all).deviations}
                        />
                      </div>
                      <div className="xl:col-span-1">
                        <DeviationTopRankingCard />
                      </div>
                    </div>
                  </div>
                </DarkCard>
              </div>

              <DarkCard title="Zones de limitations de vitesse" className="col-span-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-3">
                  {speedLimitationZones.map((zone) => (
                    <div key={zone.limitation} className={`rounded-xl border p-3 ${zone.cardClass}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className={`text-sm font-bold ${zone.titleClass}`}>{zone.title}</h4>
                        <button className="rounded-md border border-border bg-white/80 p-1">
                          <Maximize2 className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                      </div>

                      <div className="mb-2">
                        <select
                          value={weekdayFilter}
                          onChange={(e) => setWeekdayFilter(e.target.value)}
                          className="h-10 w-full rounded-md border border-border bg-white px-3 pr-9 text-sm leading-5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {WEEKDAY_OPTIONS.map((option) => (
                            <option key={`zone-${zone.limitation}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1.5 mb-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Véhicules</span>
                          <span className="font-bold text-gray-900">{zone.vehicles}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Vitesse max</span>
                          <span className="font-bold text-red-600">{zone.maxSpeed} km/h</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Vitesse moy</span>
                          <span className="font-bold text-gray-900">{zone.avgSpeed} km/h</span>
                        </div>
                      </div>

                      <div className="mb-3 h-[180px] w-full rounded-md border border-white/60 bg-white/70 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={zone.trendData.map((point) => ({
                              ...point,
                              value: Math.max(
                                0,
                                Math.round(
                                  point.value * ((DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all).incidents || 1)
                                )
                              ),
                            }))}
                          >
                            <defs>
                              <linearGradient id={`zoneGradient-${zone.limitation}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={zone.trendColor} stopOpacity={0.2} />
                                <stop offset="100%" stopColor={zone.trendColor} stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#475569" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                            <YAxis
                              tick={{ fontSize: 10, fill: "#64748b" }}
                              axisLine={false}
                              tickLine={false}
                              width={28}
                            />
                            <Tooltip />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={zone.trendColor}
                              strokeWidth={2}
                              fill={`url(#zoneGradient-${zone.limitation})`}
                              dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: zone.trendColor }}
                              activeDot={{ r: 5 }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="rounded-lg border border-border bg-white p-1.5">
                        <LimitationZoneRankingCard
                          limitation={zone.limitation}
                          colorTheme={zone.colorTheme}
                          label={`Violations ${zone.limitation}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </DarkCard>

              <div className="mt-3 grid grid-cols-12 gap-3">
                <DarkCard
                  title="Évolution horaire des Flux de Trafic et de la Vitesse Moyenne"
                  className="col-span-12 lg:col-span-6"
                >
                  <WeekdayFilterSelect
                    value={weekdayFilter}
                    onChange={(e) => setWeekdayFilter(e.target.value)}
                  />
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredMultiTrendData}>
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 3" />
                        <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="volumeTrafic" name="Volume de trafic" stroke="#ef4444" strokeWidth={3} dot={false} />
                        <Line type="monotone" dataKey="vitesseMoyenne" name="Vitesse moyenne" stroke="#06b6d4" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </DarkCard>
              </div>

          <div className="mt-4 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <b className="text-foreground">Légende:</b> Vert = circulation fluide, Jaune = dense, Rouge = congestion.
              </div>
              <div>
                <b className="text-foreground">Contact:</b> +237 000 000 000 | incidents@traffic.cm
              </div>
              <div>
                <b className="text-foreground">Aide:</b> FAQ / Guide Utilisateur
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrafficPage = forwardRef(TrafficPageImpl);
export default TrafficPage;



